import { useSessionStorage } from '@vueuse/core';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useServices } from '@/core/composables/services';
import { RouteNames } from '@/core/plugins/router/routeNames';
import {
  type MeQuery,
  type UpdateUserProfileMutationVariables,
  useJoinToSupplierMutation,
  useMeQuery,
  useRegisterKsbAccountMutation,
  useUpdateUserProfileMutation,
} from '@/graphql/generated';

export type Profile = MeQuery['me'];

const isAuth = ref(false);
const profile = ref<Profile>();

const _afterLoginPath = ref();

useSessionStorage('afterLoginPath', _afterLoginPath);

export const useAuthStore = () => {
  useServices();
  const $router = useRouter();

  const setAfterLoginPath = () => {
    _afterLoginPath.value = window.location.href.replace(window.location.origin, '');
  };
  const removeAfterLoginPath = () => {
    _afterLoginPath.value = undefined;
  };

  const afterLoginPath = () =>
    _afterLoginPath.value ? $router.resolve(_afterLoginPath.value) : { name: RouteNames.Home };

  const setAuth = (status: boolean) => {
    isAuth.value = status;
  };

  const getProfile = async (force = false) => {
    if (!isAuth.value) {
      return {
        data: null,
        errors: null,
      };
    }

    if (profile.value && !force) {
      return {
        data: {
          me: profile.value,
        },
        errors: null,
      };
    }

    const { request } = useMeQuery({});
    const response = await request();

    if (response.data) {
      profile.value = response.data.me;
    }

    return response;
  };

  const updateProfile = async (payload: UpdateUserProfileMutationVariables) => {
    const { request } = useUpdateUserProfileMutation({
      phoneNumber: payload.phoneNumber,
      name: payload.name,
      lastName: payload.lastName,
      middleName: payload.middleName,
    });

    return await request();
  };

  const joinToSupplier = async (code: string) => {
    const { request } = useJoinToSupplierMutation({
      inviteCode: code,
    });
    const response = await request();

    if (response?.errors) {
      return response;
    }

    return response;
  };

  const registerKSBAccount = async () => {
    if (!isAuth.value) {
      return undefined;
    }

    try {
      const { request } = useRegisterKsbAccountMutation({});
      const response = await request();

      return response;
    } catch (e) {
      console.log(e);
      return undefined;
    }
  };

  return {
    isAuth,
    profile,

    setAuth,

    getProfile,
    updateProfile,
    registerKSBAccount,
    joinToSupplier,

    setAfterLoginPath,
    removeAfterLoginPath,
    afterLoginPath,
  };
};
